// Office.js Word API wrapper functions
/* global Word */

import { Paper, CitationStyle } from "../types/paper";
import { formatInTextCitation } from "./citation";

/**
 * Check if Word API is available
 */
function isWordAvailable(): boolean {
  return typeof Word !== "undefined" && Word !== null;
}

/**
 * Insert a citation at the current cursor position
 * Wraps it in a Content Control for tracking
 */
export async function insertCitation(paper: Paper, style: CitationStyle = "APA"): Promise<void> {
  // Check if Word is available
  if (!isWordAvailable()) {
    const errorMsg = "Word API is not available. Make sure you're running this in Microsoft Word.";
    console.error("❌", errorMsg);
    throw new Error(errorMsg);
  }

  try {
    await Word.run(async (context) => {
      // Get the current selection (cursor position)
      const range = context.document.getSelection();

      // Format the citation text
      const citationText = formatInTextCitation(paper, style);

      // Insert the citation text
      const citationRange = range.insertText(citationText, Word.InsertLocation.end);

      // Wrap it in a Content Control for tracking
      const contentControl = citationRange.insertContentControl();
      contentControl.tag = "scholarsync-citation";
      contentControl.title = paper.id; // Store paper ID
      contentControl.appearance = "BoundingBox"; // Subtle border instead of tags
      contentControl.color = "#e0e0e0"; // Light gray border

      // Load properties to verify
      contentControl.load("tag,title");

      await context.sync();

      console.log(`✅ Citation inserted: ${citationText} [${paper.id}]`);
    });
  } catch (error) {
    console.error("❌ Error inserting citation:", error);
    throw error;
  }
}

/**
 * Find all Scholar Sync citations in the document
 * Returns array of paper IDs (handles both single and multiple citations)
 */
export async function findAllCitations(): Promise<string[]> {
  try {
    const paperIds: string[] = [];
    let singleCount = 0;
    let multiCount = 0;

    await Word.run(async (context) => {
      // Find all single citation content controls
      const singleControls = context.document.contentControls.getByTag("scholarsync-citation");
      singleControls.load("items");

      // Find all multi-citation content controls
      const multiControls = context.document.contentControls.getByTag("scholarsync-citation-multiple");
      multiControls.load("items");

      await context.sync();

      singleCount = singleControls.items.length;
      multiCount = multiControls.items.length;

      // Extract paper IDs from single citations
      for (let i = 0; i < singleControls.items.length; i++) {
        const control = singleControls.items[i];
        control.load("title");
        await context.sync();

        if (control.title) {
          paperIds.push(control.title);
        }
      }

      // Extract paper IDs from multi-citations (comma-separated)
      for (let i = 0; i < multiControls.items.length; i++) {
        const control = multiControls.items[i];
        control.load("title");
        await context.sync();

        if (control.title) {
          // Split comma-separated IDs and add them all
          const ids = control.title.split(',').map(id => id.trim());
          paperIds.push(...ids);
        }
      }
    });

    console.log(`📚 Found ${paperIds.length} citations in document (${singleCount} single, ${multiCount} multi)`);
    return paperIds;
  } catch (error) {
    console.error("❌ Error finding citations:", error);
    return [];
  }
}

/**
 * Insert a bibliography at the end of the document
 */
export async function insertBibliography(references: string[]): Promise<void> {
  try {
    await Word.run(async (context) => {
      const body = context.document.body;

      // Add heading with Normal style
      const heading = body.insertParagraph("References", Word.InsertLocation.end);
      heading.styleBuiltIn = Word.BuiltInStyleName.normal;
      heading.font.size = 12; // Standard size

      // Add each reference
      references.forEach((reference) => {
        const para = body.insertParagraph(reference, Word.InsertLocation.end);
        para.leftIndent = 36; // Hanging indent (0.5 inch)
        para.firstLineIndent = -36;
      });

      await context.sync();

      console.log(`📖 Bibliography inserted with ${references.length} references`);
    });
  } catch (error) {
    console.error("❌ Error inserting bibliography:", error);
    throw error;
  }
}
